// "Manuals & SOPs" box on a record that manuals can be linked to (equipment, maintenance task, risk assessment, checklist / contingency
// plan, drill, regulation, another manual) - the other side of the links made on the Manuals and SOPs pages (sql/manuals.sql,
// sql/sop-layer.sql manual_links). View sees the linked manuals / SOPs and opens them (an SOP on the SOPs page); Edit links /
// unlinks. Shows nothing at all when the login has no access to Manuals or the SQL has not run yet.
import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { rights, linkKindOf, statusInfo, isArchived, isSop, docLabel } from '../lib/manualsCore'
import { openManualFile } from '../lib/manualFiles'

const levels = {}   // vessel -> level, for this page load
export function useManualsLevel(clientId) {
  const [lvl, setLvl] = useState(clientId && levels[clientId] != null ? levels[clientId] : null)
  useEffect(() => {
    if (!clientId) return
    if (levels[clientId] != null) { setLvl(levels[clientId]); return }
    let live = true
    supabase.rpc('my_perm', { p_client: String(clientId), p_module: 'manuals' }).then(({ data }) => { levels[clientId] = Number(data) || 0; if (live) setLvl(levels[clientId]) }, () => { if (live) setLvl(0) })
    return () => { live = false }
  }, [clientId])
  return lvl
}
export function openManual(id, sop = false) {
  try { localStorage.setItem(sop ? 'hc_open_sop' : 'hc_open_manual', String(id)) } catch (e) {}
  window.dispatchEvent(new CustomEvent('hc:navigate', { detail: { page: sop ? 'sops' : 'manuals' } }))
}

export default function LinkedManuals({ kind, targetId, title }) {
  const { config } = useAuth()
  const cid = config?.id
  const lvl = useManualsLevel(cid)
  const r = rights(lvl || 0)
  const [rows, setRows] = useState(null)    // [{ link, manual }]
  const [all, setAll] = useState(null)      // every active manual (for the picker)
  const [pick, setPick] = useState('')
  const [err, setErr] = useState('')
  async function load() {
    if (!cid || !targetId) return
    const { data: links, error } = await supabase.from('manual_links').select('id,manual_id,kind,target_id,note').eq('client_id', cid).eq('kind', kind).eq('target_id', String(targetId))
    if (error) { setRows(false); return }
    const ids = (links || []).map(l => l.manual_id)
    let ms = []
    if (ids.length) {
      let x = await supabase.from('manuals').select('id,title,status,archived_at,version,category,file_url,doc_kind,doc_number').in('id', ids)
      if (x.error) x = await supabase.from('manuals').select('id,title,status,archived_at,version,category,file_url').in('id', ids)   // before sql/sop-layer.sql
      ms = x.data || []
    }
    setRows((links || []).map(l => ({ link: l, manual: ms.find(m => m.id === l.manual_id) })).filter(x => x.manual && !isArchived(x.manual)))
  }
  useEffect(() => { setRows(null); load() }, [cid, kind, targetId]) // eslint-disable-line
  useEffect(() => { if (r.link && all == null) supabase.from('manuals').select('*').eq('client_id', cid).is('archived_at', null).order('title').then(x => setAll((x.data || []).filter(m => !(kind === 'manual' && String(m.id) === String(targetId))))) }, [r.link]) // eslint-disable-line
  if (rows === false || (lvl != null && !r.view)) return null
  if (rows == null) return null
  if (!rows.length && !r.link) return null
  async function add() {
    if (!pick) return
    setErr('')
    const { error } = await supabase.from('manual_links').insert({ manual_id: pick, client_id: cid, kind, target_id: String(targetId), title: String(title || targetId).slice(0, 160) })
    if (error) { setErr(error.message); return }
    setPick(''); load()
  }
  async function remove(id) {
    setErr('')
    const { error } = await supabase.from('manual_links').delete().eq('id', id)
    if (error) setErr(error.message); else load()
  }
  const free = (all || []).filter(m => !rows.some(x => x.manual.id === m.id))
  return (
    <div style={{ marginTop: 14, padding: '10px 12px', border: '1px solid var(--line, var(--border))', borderRadius: 10, background: 'var(--card, var(--hull))' }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6 }}>{kind === 'manual' ? 'SOPs and manuals that cite this one' : 'Manuals & SOPs'}</div>
      {!rows.length && <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>No manual or SOP is linked to this {linkKindOf(kind).label.toLowerCase()} yet.</div>}
      {rows.map(({ link, manual }) => (
        <div key={link.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13 }}>
          <button type="button" className="btn" style={{ padding: '3px 9px', fontSize: 12.5 }} onClick={() => openManual(manual.id, isSop(manual))} title={isSop(manual) ? 'Open this SOP' : 'Open this manual'}>{isSop(manual) && <b style={{ color: '#2457d6', marginRight: 5 }}>SOP</b>}{docLabel(manual)}</button>
          {manual.file_url && manual.status === 'approved' && !isSop(manual) && <button type="button" className="btn" style={{ padding: '3px 9px', fontSize: 12 }} onClick={() => openManualFile(manual.file_url + (/^p\.?\s*(\d+)$/i.test(link.note || '') ? '#page=' + link.note.match(/(\d+)/)[1] : ''), manual.id)} aria-label={'Open the file of ' + manual.title}>Open file</button>}
          <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>v{manual.version || 1} · {statusInfo(manual).label}{link.note ? ' · ' + link.note : ''}</span>
          {r.link && <button type="button" className="btn" style={{ marginLeft: 'auto', padding: '2px 8px', fontSize: 12, color: 'var(--red)' }} onClick={() => remove(link.id)} aria-label={'Unlink ' + manual.title}>Unlink</button>}
        </div>
      ))}
      {r.link && free.length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          <select value={pick} onChange={e => setPick(e.target.value)} aria-label="Manual to link" style={{ flex: 1, minWidth: 0, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--line, var(--border))', background: 'var(--card, var(--hull))', color: 'var(--text)', fontSize: 12.5 }}>
            <option value="">Link a manual or SOP…</option>
            {free.map(m => <option key={m.id} value={m.id}>{isSop(m) ? 'SOP ' : ''}{docLabel(m)}</option>)}
          </select>
          <button type="button" className="btn" disabled={!pick} onClick={add}>Link</button>
        </div>
      )}
      {err && <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 6 }}>{err}</div>}
    </div>
  )
}
